/**
 * The Web Push service worker's scope, in ONE place.
 *
 * Two files must agree on it and they sit at opposite ends of the app:
 * `main.tsx` sweeps away stale service workers at every boot, and
 * `lib/web-push.ts` registers the push worker. When the sweep did not know
 * about this scope it unregistered the push worker too, and a push
 * subscription dies with its registration — so push worked until the user's
 * next page load and no further. Measured on eventripple.app 11.08.2026:
 * registrations went from ['https://eventripple.app/push-sw-scope/'] to []
 * after one boot. `push_subscriptions` has never held a row.
 *
 * Kept dependency-free on purpose. `main.tsx` runs the sweep BEFORE React
 * mounts, so importing `web-push.ts` there (which pulls in the Supabase
 * client) would move module initialisation into the boot path.
 */
export const PUSH_SW_SCOPE = '/push-sw-scope/'
export const PUSH_SW_URL = '/push-sw-scope/push-sw.js'

/**
 * True when a ServiceWorkerRegistration scope belongs to the push worker.
 *
 * `registration.scope` is an absolute URL (`https://host/push-sw-scope/`),
 * so compare pathnames rather than strings. The base is only needed to parse
 * a relative value and never reaches the comparison.
 */
export function isPushServiceWorkerScope(scope: string): boolean {
  try {
    return new URL(scope, 'http://localhost').pathname === PUSH_SW_SCOPE
  } catch {
    return false
  }
}

/**
 * Unregister every service worker EXCEPT the push worker, and clear all caches.
 *
 * The freshness contract (CLAUDE.md) requires this at every boot: without it a
 * user carrying a stale precaching worker is served old HTML and JS for days.
 * Exempting the push scope does not weaken it, by construction rather than by
 * judgement: `push-sw.js` registers no `fetch` handler and its scope contains
 * no pages, so it can never answer a navigation. Measured on eventripple.app
 * with it registered and active, `navigator.serviceWorker.controller` was null
 * on /dashboard.
 *
 * Lives here, not inline in `main.tsx`, so the exemption is covered by a test
 * rather than by a comment asking the next reader not to delete it.
 */
export function sweepStaleServiceWorkers(scope: {
  navigator?: { serviceWorker?: { getRegistrations(): Promise<readonly { scope: string; unregister(): unknown }[]> } }
  caches?: { keys(): Promise<string[]>; delete(key: string): Promise<boolean> }
} = globalThis as never): void {
  const sw = scope.navigator?.serviceWorker
  if (sw) {
    sw.getRegistrations()
      .then((regs) =>
        regs.forEach((r) => {
          if (isPushServiceWorkerScope(r.scope)) return
          r.unregister()
        })
      )
      .catch(() => {})
  }

  // Caches are swept whole. The push worker opens none: it has no fetch
  // handler and never calls caches.open, so there is nothing of its to spare.
  const caches = scope.caches
  if (caches) {
    caches
      .keys()
      .then((keys) => Promise.all(keys.map((k) => caches.delete(k))))
      .catch(() => {})
  }
}
